import {
  AuthConfigSchema,
  type AuthConfig,
  AuthMeSchema,
  type AuthMe,
  OkSchema,
  type DevLoginInput,
  type ParticipantLanguageInput,
  LandingStateSchema,
  type LandingState,
  DrawResultSchema,
  type DrawResult,
  type PrizeKindDto,
  DrawErrorSchema,
  type DrawErrorCode,
  LandingAwardsResponseSchema,
  type LandingAwardsResponse,
  AdminMeSchema,
  type AdminMe,
  AdminAllowedEmailsResponseSchema,
  type AdminAllowedEmailsResponse,
  type AdminRequestCodeInput,
  type AdminVerifyCodeInput,
  type AdminAllowedEmailInput,
  TicketStatusCountsSchema,
  type TicketStatusCounts,
  TicketSyncStatusSchema,
  type TicketSyncStatus,
  TicketSearchResponseSchema,
  type TicketSearchResponse,
  type TicketSearchBy,
  TicketEventsResponseSchema,
  type TicketEventsResponse,
  ManualActionSchema,
  type ManualActionDto,
  type ManualTicketsInputDto,
  ParticipantProfileSchema,
  type ParticipantProfileDto,
  TicketBalanceSchema,
  type TicketBalance,
  AdminDrawPlanSchema,
  type AdminDrawPlan,
  AdminPrizeItemsResponseSchema,
  AdminPrizeItemDetailSchema,
  type AdminPrizeItemDetail,
  AdminPrizeItemSchema,
  type AdminPrizeItem,
  type AdminPrizeItemPatch,
  AdminPrizeLotSchema,
  type AdminPrizeLot,
  type AdminPrizeLotPatch,
  AdminGroupSharesSchema,
  type AdminGroupShares,
  AdminDrawCurveSchema,
  type AdminDrawCurve,
  AdminCatalogChangesResponseSchema,
  AdminAwardsResponseSchema,
  AdminAwardSchema,
  type AdminAward,
  BlockedAwardsResponseSchema,
  AdminParticipantCardSchema,
  type AdminParticipantCard,
  ParticipationResetSchema,
  type ParticipationResetDto,
  AdminParticipantsResponseSchema,
  type AdminParticipantsResponse,
  PrizeTranslationsSchema,
  type PrizeTranslations,
  type PrizeTranslationsPatch,
  AdminTranslationsStatusResponseSchema,
  type AdminTranslationsStatusResponse,
  AdminTranslationsImportResultSchema,
  type AdminTranslationsImportResult,
  PrizeMessageTextsSchema,
  type PrizeMessageTexts,
  type PrizeMessageTextsPatch,
  PrizeMessageTranslationsSchema,
  type PrizeMessageTranslations,
  type PrizeMessageTranslationsPatch,
  PrizeMessagePreviewResponseSchema,
  type PrizeMessagePreviewResponse,
  type PrizeMessagePreviewInput,
  AdminMessageTranslationsStatusResponseSchema,
  type AdminMessageTranslationsStatusResponse,
  AdminMessageTranslationsImportResultSchema,
  type AdminMessageTranslationsImportResult,
  AdminNlOutboxResponseSchema,
  type AdminNlOutboxResponse,
  AdminNlOutboxMessageSchema,
  type AdminNlOutboxMessage,
} from '~shared/schemas';

/**
 * Типизированный API-клиент.
 *
 * Паттерн каждого метода:
 *   1. fetch() → берём JSON.
 *   2. ZodSchema.parse() — валидируем, что сервер вернул именно то, что обещал.
 *   3. Возвращаем типизированное значение.
 *
 * Ошибки:
 *   - Не-2xx кидает ApiError с status и body.
 *   - Невалидный JSON кидает ZodError — react-query ловит автоматически.
 */

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: unknown) {
    super(`HTTP ${status}`);
    this.name = 'ApiError';
  }
}

/** Код отказа розыгрыша из тела ошибки; null — другая ошибка (сеть, 401, 5xx без кода). */
export function drawErrorCode(err: unknown): DrawErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  const parsed = DrawErrorSchema.safeParse(err.body);
  return parsed.success ? parsed.data.code : null;
}

async function request<T>(
  path: string,
  schema: { parse: (x: unknown) => T },
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }
  const json = await res.json();
  return schema.parse(json);
}

function postJson<T>(path: string, schema: { parse: (x: unknown) => T }, body: unknown): Promise<T> {
  return sendJson('POST', path, schema, body);
}

function putJson<T>(path: string, schema: { parse: (x: unknown) => T }, body: unknown): Promise<T> {
  return sendJson('PUT', path, schema, body);
}

function sendJson<T>(method: 'POST' | 'PUT', path: string, schema: { parse: (x: unknown) => T }, body: unknown): Promise<T> {
  return request(path, schema, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

function deleteRequest<T>(path: string, schema: { parse: (x: unknown) => T }): Promise<T> {
  return request(path, schema, { method: 'DELETE' });
}

export const api = {
  auth: {
    config(): Promise<AuthConfig> {
      return request('/api/auth/config', AuthConfigSchema);
    },
    /** Текущий участник; null — входа нет (401). */
    async me(): Promise<AuthMe | null> {
      try {
        return await request('/api/auth/me', AuthMeSchema);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    logout(): Promise<{ ok: true }> {
      return postJson('/api/auth/logout', OkSchema, {});
    },
    devLogin(input: DevLoginInput): Promise<{ ok: true }> {
      return postJson('/api/auth/dev-login', OkSchema, input);
    },
    /** Язык лендинга вошедшего участника — на нём пойдут сообщения ему. */
    saveLanguage(input: ParticipantLanguageInput): Promise<{ ok: true }> {
      return putJson('/api/auth/me/language', OkSchema, input);
    },
  },
  landing: {
    /** Участие и баланс козочек вошедшего участника; null — входа нет (401). */
    async state(): Promise<LandingState | null> {
      try {
        return await request('/api/landing/state', LandingStateSchema);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    confirmParticipation(): Promise<{ ok: true }> {
      return postJson('/api/landing/participation', OkSchema, {});
    },
    /** Открытие козочки: выигрыш, 8 призов для остальных ячеек и неоткрытый остаток. Отказ — ApiError, код — drawErrorCode(). */
    draw(): Promise<DrawResult> {
      return postJson('/api/landing/draw', DrawResultSchema, {});
    },
    /** Действующие выигрыши вошедшего участника, новые сверху. */
    awards(): Promise<LandingAwardsResponse> {
      return request('/api/landing/awards', LandingAwardsResponseSchema);
    },
  },
  adminAuth: {
    requestCode(input: AdminRequestCodeInput): Promise<{ ok: true }> {
      return postJson('/api/admin/auth/request-code', OkSchema, input);
    },
    verifyCode(input: AdminVerifyCodeInput): Promise<{ ok: true }> {
      return postJson('/api/admin/auth/verify-code', OkSchema, input);
    },
    /** Текущий сотрудник в сессии; null — входа нет (401). */
    async me(): Promise<AdminMe | null> {
      try {
        return await request('/api/admin/auth/me', AdminMeSchema);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    logout(): Promise<{ ok: true }> {
      return postJson('/api/admin/auth/logout', OkSchema, {});
    },
  },
  adminAllowedEmails: {
    list(): Promise<AdminAllowedEmailsResponse> {
      return request('/api/admin/allowed-emails', AdminAllowedEmailsResponseSchema);
    },
    add(input: AdminAllowedEmailInput): Promise<{ ok: true }> {
      return postJson('/api/admin/allowed-emails', OkSchema, input);
    },
    rename(oldEmail: string, newEmail: string): Promise<{ ok: true }> {
      return putJson(`/api/admin/allowed-emails/${encodeURIComponent(oldEmail)}`, OkSchema, { email: newEmail });
    },
    remove(email: string): Promise<{ ok: true }> {
      return deleteRequest(`/api/admin/allowed-emails/${encodeURIComponent(email)}`, OkSchema);
    },
  },
  adminTickets: {
    statusCounts(): Promise<TicketStatusCounts> {
      return request('/api/admin/tickets/status-counts', TicketStatusCountsSchema);
    },
    syncStatus(): Promise<TicketSyncStatus> {
      return request('/api/admin/ticket-sync/status', TicketSyncStatusSchema);
    },
    /** Без value — последние начисленные билеты. */
    search(by: TicketSearchBy, value: string, offset = 0): Promise<TicketSearchResponse> {
      const q = new URLSearchParams({ offset: String(offset) });
      if (value) {
        q.set('by', by);
        q.set('value', value);
      }
      return request(`/api/admin/tickets/search?${q}`, TicketSearchResponseSchema);
    },
    /** ticketId — журнал билета; by+value — по критерию; ничего — общая лента. */
    events(filter: { ticketId?: number; by?: TicketSearchBy; value?: string }, offset = 0): Promise<TicketEventsResponse> {
      const q = new URLSearchParams({ offset: String(offset) });
      if (filter.ticketId !== undefined) q.set('ticketId', String(filter.ticketId));
      if (filter.by && filter.value) {
        q.set('by', filter.by);
        q.set('value', filter.value);
      }
      return request(`/api/admin/tickets/events?${q}`, TicketEventsResponseSchema);
    },
    profile(participantId: string): Promise<ParticipantProfileDto> {
      return request(`/api/admin/tickets/participants/${encodeURIComponent(participantId)}/profile`, ParticipantProfileSchema);
    },
    balance(participantId: string): Promise<TicketBalance> {
      return request(`/api/admin/tickets/participants/${encodeURIComponent(participantId)}/balance`, TicketBalanceSchema);
    },
  },
  adminDraw: {
    plan(country: string, month: string, week?: string): Promise<AdminDrawPlan> {
      const q = new URLSearchParams({ country, month });
      if (week) q.set('week', week);
      return request(`/api/admin/draw/plan?${q}`, AdminDrawPlanSchema);
    },
    items(filter: { q?: string; kind?: PrizeKindDto; physicalWithoutSku?: boolean }, offset = 0) {
      const q = new URLSearchParams({ offset: String(offset) });
      if (filter.q) q.set('q', filter.q);
      if (filter.kind) q.set('kind', filter.kind);
      if (filter.physicalWithoutSku) q.set('physicalWithoutSku', 'true');
      return request(`/api/admin/draw/items?${q}`, AdminPrizeItemsResponseSchema);
    },
    item(itemId: number): Promise<AdminPrizeItemDetail> {
      return request(`/api/admin/draw/items/${itemId}`, AdminPrizeItemDetailSchema);
    },
    updateItem(itemId: number, patch: AdminPrizeItemPatch): Promise<AdminPrizeItem> {
      return putJson(`/api/admin/draw/items/${itemId}`, AdminPrizeItemSchema, patch);
    },
    updateLot(prizeId: number, patch: AdminPrizeLotPatch): Promise<AdminPrizeLot> {
      return putJson(`/api/admin/draw/prizes/${prizeId}`, AdminPrizeLotSchema, patch);
    },
    itemTranslations(itemId: number): Promise<PrizeTranslations> {
      return request(`/api/admin/draw/items/${itemId}/translations`, PrizeTranslationsSchema);
    },
    updateItemTranslations(itemId: number, patch: PrizeTranslationsPatch): Promise<PrizeTranslations> {
      return putJson(`/api/admin/draw/items/${itemId}/translations`, PrizeTranslationsSchema, patch);
    },
    lotTranslations(prizeId: number): Promise<PrizeTranslations> {
      return request(`/api/admin/draw/prizes/${prizeId}/translations`, PrizeTranslationsSchema);
    },
    updateLotTranslations(prizeId: number, patch: PrizeTranslationsPatch): Promise<PrizeTranslations> {
      return putJson(`/api/admin/draw/prizes/${prizeId}/translations`, PrizeTranslationsSchema, patch);
    },
    translationsStatus(): Promise<AdminTranslationsStatusResponse> {
      return request('/api/admin/draw/translations/status', AdminTranslationsStatusResponseSchema);
    },
    /** Выгрузка CSV — страница переводов сама инициирует скачивание файла браузером. */
    async exportTranslationsCsv(): Promise<Blob> {
      const res = await fetch('/api/admin/draw/translations/export');
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      return res.blob();
    },
    async importTranslationsCsv(file: File): Promise<AdminTranslationsImportResult> {
      const text = await file.text();
      return request('/api/admin/draw/translations/import', AdminTranslationsImportResultSchema, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: text,
      });
    },
    shares(country: string, month: string): Promise<AdminGroupShares> {
      return request(`/api/admin/draw/group-shares?${new URLSearchParams({ country, month })}`, AdminGroupSharesSchema);
    },
    saveShares(input: AdminGroupShares): Promise<{ ok: true }> {
      return putJson('/api/admin/draw/group-shares', OkSchema, input);
    },
    curve(): Promise<AdminDrawCurve> {
      return request('/api/admin/draw/curve', AdminDrawCurveSchema);
    },
    saveCurve(input: AdminDrawCurve): Promise<{ ok: true }> {
      return putJson('/api/admin/draw/curve', OkSchema, input);
    },
    changes(offset = 0) {
      return request(`/api/admin/draw/catalog-changes?offset=${offset}`, AdminCatalogChangesResponseSchema);
    },
    awards(filter: { participantId?: string; prizeId?: string; country?: string; month?: string; week?: string }, offset = 0) {
      const q = new URLSearchParams({ offset: String(offset) });
      for (const [k, v] of Object.entries(filter)) if (v) q.set(k, v);
      return request(`/api/admin/draw/awards?${q}`, AdminAwardsResponseSchema);
    },
    cancelAward(awardId: number, reason: string): Promise<AdminAward> {
      return postJson(`/api/admin/draw/awards/${awardId}/cancel`, AdminAwardSchema, { reason });
    },
    blocked(offset = 0) {
      return request(`/api/admin/draw/blocked?offset=${offset}`, BlockedAwardsResponseSchema);
    },
    restoreAward(awardId: number, reason: string): Promise<AdminAward> {
      return postJson(`/api/admin/draw/awards/${awardId}/restore`, AdminAwardSchema, { reason });
    },
  },
  adminPrizeMessages: {
    kindTexts(kind: PrizeKindDto): Promise<PrizeMessageTexts> {
      return request(`/api/admin/draw/kinds/${kind}/messages`, PrizeMessageTextsSchema);
    },
    updateKindTexts(kind: PrizeKindDto, patch: PrizeMessageTextsPatch): Promise<PrizeMessageTexts> {
      return putJson(`/api/admin/draw/kinds/${kind}/messages`, PrizeMessageTextsSchema, patch);
    },
    kindTranslations(kind: PrizeKindDto): Promise<PrizeMessageTranslations> {
      return request(`/api/admin/draw/kinds/${kind}/messages/translations`, PrizeMessageTranslationsSchema);
    },
    updateKindTranslations(kind: PrizeKindDto, patch: PrizeMessageTranslationsPatch): Promise<PrizeMessageTranslations> {
      return putJson(`/api/admin/draw/kinds/${kind}/messages/translations`, PrizeMessageTranslationsSchema, patch);
    },
    previewKind(kind: PrizeKindDto, input: PrizeMessagePreviewInput): Promise<PrizeMessagePreviewResponse> {
      return postJson(`/api/admin/draw/kinds/${kind}/messages/preview`, PrizeMessagePreviewResponseSchema, input);
    },
    itemTexts(itemId: number): Promise<PrizeMessageTexts> {
      return request(`/api/admin/draw/items/${itemId}/messages`, PrizeMessageTextsSchema);
    },
    updateItemTexts(itemId: number, patch: PrizeMessageTextsPatch): Promise<PrizeMessageTexts> {
      return putJson(`/api/admin/draw/items/${itemId}/messages`, PrizeMessageTextsSchema, patch);
    },
    itemTranslations(itemId: number): Promise<PrizeMessageTranslations> {
      return request(`/api/admin/draw/items/${itemId}/messages/translations`, PrizeMessageTranslationsSchema);
    },
    updateItemTranslations(itemId: number, patch: PrizeMessageTranslationsPatch): Promise<PrizeMessageTranslations> {
      return putJson(`/api/admin/draw/items/${itemId}/messages/translations`, PrizeMessageTranslationsSchema, patch);
    },
    previewItem(itemId: number, input: PrizeMessagePreviewInput): Promise<PrizeMessagePreviewResponse> {
      return postJson(`/api/admin/draw/items/${itemId}/messages/preview`, PrizeMessagePreviewResponseSchema, input);
    },
    translationsStatus(): Promise<AdminMessageTranslationsStatusResponse> {
      return request('/api/admin/draw/messages/translations/status', AdminMessageTranslationsStatusResponseSchema);
    },
    async exportTranslationsCsv(): Promise<Blob> {
      const res = await fetch('/api/admin/draw/messages/translations/export');
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      return res.blob();
    },
    async importTranslationsCsv(file: File): Promise<AdminMessageTranslationsImportResult> {
      const text = await file.text();
      return request('/api/admin/draw/messages/translations/import', AdminMessageTranslationsImportResultSchema, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: text,
      });
    },
  },
  adminNlOutbox: {
    list(filter: { type?: string; status?: string }, offset = 0): Promise<AdminNlOutboxResponse> {
      const q = new URLSearchParams({ offset: String(offset) });
      for (const [k, v] of Object.entries(filter)) if (v) q.set(k, v);
      return request(`/api/admin/nl-outbox?${q}`, AdminNlOutboxResponseSchema);
    },
    get(id: number): Promise<AdminNlOutboxMessage> {
      return request(`/api/admin/nl-outbox/${id}`, AdminNlOutboxMessageSchema);
    },
    /** Возвращает сообщение, которое уйдёт в НЛ: то же или новую копию. */
    retry(id: number): Promise<AdminNlOutboxMessage> {
      return postJson(`/api/admin/nl-outbox/${id}/retry`, AdminNlOutboxMessageSchema, {});
    },
    cancel(id: number): Promise<AdminNlOutboxMessage> {
      return postJson(`/api/admin/nl-outbox/${id}/cancel`, AdminNlOutboxMessageSchema, {});
    },
  },
  adminParticipants: {
    list(filter: { q?: string; visited?: string; confirmed?: string }, offset = 0): Promise<AdminParticipantsResponse> {
      const q = new URLSearchParams({ offset: String(offset) });
      for (const [k, v] of Object.entries(filter)) if (v) q.set(k, v);
      return request(`/api/admin/participants?${q}`, AdminParticipantsResponseSchema);
    },
    get(participantId: string): Promise<AdminParticipantCard> {
      return request(`/api/admin/participants/${encodeURIComponent(participantId)}`, AdminParticipantCardSchema);
    },
    /** Ручное начисление или списание козочек (ticket-accrual, «Ручное начисление/списание козочек»). */
    manualTickets(participantId: string, op: 'grant' | 'revoke', input: ManualTicketsInputDto): Promise<ManualActionDto> {
      return postJson(`/api/admin/participants/${encodeURIComponent(participantId)}/tickets/${op}`, ManualActionSchema, input);
    },
    resetParticipation(participantId: string, reason: string): Promise<ParticipationResetDto> {
      return postJson(`/api/admin/participants/${encodeURIComponent(participantId)}/reset-participation`, ParticipationResetSchema, { reason });
    },
  },
};

/** Адрес начала входа через SSO NL — обычная ссылка, сервер сам перенаправит на ng.nlstar.com. */
export const SSO_LOGIN_URL = '/api/auth/sso/login';
